import { useState } from 'react';
import { StudioButton as Button } from './styles';
import { safeSource } from './safeSource';
import type { AuthoredAttachments } from './contracts';
import { Field, Grid, Note, Row } from './styles';
export type AttachmentTool = 'context' | 'cherishPrompt' | 'mood';
export function AttachmentEditor({
  kind,
  value,
  onSave,
}: {
  kind: AttachmentTool;
  value: AuthoredAttachments;
  onSave: (next: AuthoredAttachments, insert?: string) => void;
}) {
  const [body, setBody] = useState(
    kind === 'context' ? (value.context?.body ?? '') : (value.cherishPrompt ?? ''),
  );
  const [sources, setSources] = useState(value.context?.sources ?? []);
  const [label, setLabel] = useState(value.mood?.label ?? '');
  const [emoji, setEmoji] = useState(value.mood?.emoji ?? '');
  const [publicLabel, setPublicLabel] = useState(true);
  const [insert, setInsert] = useState(false);
  const [error, setError] = useState('');
  function save() {
    const next = { ...value };
    if (kind === 'context') {
      if ((!body.trim() && !sources.length) || sources.some((s) => !safeSource(s.url))) {
        setError('Add background or a valid http/https source link.');
        return;
      }
      next.context = { body: body.trim(), sources };
    } else if (kind === 'cherishPrompt') {
      if (!body.trim()) {
        setError('Write an invitation to respond.');
        return;
      }
      next.cherishPrompt = body.trim();
    } else {
      if (
        (!publicLabel && !insert) ||
        (publicLabel && !label.trim()) ||
        (insert && !emoji.trim())
      ) {
        setError('Choose a display option and fill in its label or emoji.');
        return;
      }
      if (publicLabel) next.mood = { label: label.trim(), emoji: emoji.trim() };
      else delete next.mood;
    }
    onSave(next, kind === 'mood' && insert ? emoji : undefined);
  }
  return (
    <>
      <Note>
        {kind === 'context'
          ? 'Optional background and sources, written by you and visible with your post.'
          : kind === 'cherishPrompt'
            ? 'Invite a conversation in your own words. Readers can reply freely.'
            : 'Choose a public feeling or activity label, insert an emoji into your text, or use both.'}
      </Note>
      {kind === 'mood' ? (
        <>
          <Grid>
            <Field>
              Feeling or activity
              <input
                maxLength={80}
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="Feeling hopeful"
              />
            </Field>
            <Field>
              Emoji
              <input maxLength={16} value={emoji} onChange={(e) => setEmoji(e.target.value)} />
            </Field>
          </Grid>
          <Row>
            <label>
              <input
                type="checkbox"
                checked={publicLabel}
                onChange={(e) => setPublicLabel(e.target.checked)}
              />{' '}
              Show public label
            </label>
            <label>
              <input
                type="checkbox"
                checked={insert}
                onChange={(e) => setInsert(e.target.checked)}
              />{' '}
              Insert emoji into text
            </label>
          </Row>
        </>
      ) : (
        <Field>
          {kind === 'context' ? 'Background' : 'Invitation'}
          <textarea
            maxLength={kind === 'context' ? 2000 : 320}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={
              kind === 'cherishPrompt'
                ? 'What am I missing?'
                : 'What would help someone understand this post?'
            }
          />
        </Field>
      )}
      {kind === 'context' && (
        <>
          {sources.map((s, i) => (
            <Grid key={i}>
              <Field>
                Source URL {i + 1}
                <input
                  type="url"
                  maxLength={2048}
                  value={s.url}
                  onChange={(e) =>
                    setSources(sources.map((v, n) => (n === i ? { ...v, url: e.target.value } : v)))
                  }
                />
              </Field>
              <Field>
                Source label {i + 1}
                <input
                  maxLength={120}
                  value={s.label}
                  onChange={(e) =>
                    setSources(
                      sources.map((v, n) => (n === i ? { ...v, label: e.target.value } : v)),
                    )
                  }
                />
              </Field>
              <Button variant="ghost" onClick={() => setSources(sources.filter((_, n) => n !== i))}>
                Remove source {i + 1}
              </Button>
            </Grid>
          ))}
          <Button
            disabled={sources.length >= 8}
            onClick={() => setSources([...sources, { url: '', label: '' }])}
          >
            Add source
          </Button>
        </>
      )}
      {error && <p role="alert">{error}</p>}
      <Row>
        <Button variant="primary" onClick={save}>
          Use {kind === 'cherishPrompt' ? 'prompt' : kind}
        </Button>
        <Button
          variant="ghost"
          onClick={() => {
            const next = { ...value };
            delete next[kind];
            onSave(next);
          }}
        >
          Remove attachment
        </Button>
      </Row>
    </>
  );
}
